import Image from "next/image";
import { motion, AnimatePresence } from "motion/react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useSession } from "next-auth/react";
import { toast } from "react-toastify";
import dynamic from "next/dynamic";
import ChestOpeningModalV4 from "./ChestOpeningModalV4";
import { eventSounds } from "@/components/AvatarEvent/eventSounds";
import { useRouter, Link } from "@/i18n/navigation";

// Ce que le coffre contient en plus pendant un événement de puzzles.
const PUZZLE_REWARD = [{ type: "puzzle_piece", amount: 1 }];

// La révélation d'une pièce de puzzle n'est téléchargée que pendant un événement, au premier coffre ouvert.
const EventPieceReveal = dynamic(() => import("@/components/AvatarEvent/EventPieceReveal"), { ssr: false });

// `event` : l'événement de puzzles en cours, s'il y en a un. Le coffre prend alors ses couleurs
// et la ligne dit qu'il contient une pièce de puzzle.
// `pieceLeft` : faux quand le membre a déjà toutes les pièces de l'événement. Le coffre garde alors son
// habillage, mais on ne lui promet plus une pièce qu'il ne recevra pas.
export default function QuestItem({ quest, onComplete, onClaim, variant, event = null, pieceLeft = true }) {
    const t = useTranslations();
    const [isClaiming, setIsClaiming] = useState(false);
    const [showChestModal, setShowChestModal] = useState(false);
    // Pendant un événement de puzzles, le coffre donne aussi une pièce : elle se montre une fois le coffre refermé.
    const [eventPiece, setEventPiece] = useState(null);
    const [showEventPiece, setShowEventPiece] = useState(false);
    // Fragments, XP et boucliers de la barre du haut viennent de la session :
    // on la relit à la fermeture du coffre, sinon le butin n'y apparaît qu'au
    // retour sur l'onglet.
    const { update } = useSession();
    const closeChest = () => {
        setShowChestModal(false);
        if (eventPiece) setShowEventPiece(true);
        update().catch(() => {});
    };
    const closeEventPiece = () => {
        setEventPiece(null);
        setShowEventPiece(false);
    };
    const [claimedRewards, setClaimedRewards] = useState(null);
    const router = useRouter();
    const eventChests = event?.theme?.chests || null;
    const chestSrc = eventChests?.[quest.rewardChestType === "gold" ? "legendary" : "common"] || "/static/icons/chests/common.svg";
    // Le coffre de l'événement est montré plus grand que le coffre de base : c'est lui qu'on vient ouvrir.
    const chestSize = (base) => (eventChests ? Math.round(base * 1.5) : base);
    const chestAlt = event ? t("avatar_event_quest_chest") : undefined;

    const getIcon = () => {
        switch (quest.type) {
            case "xp_gain":
                return (
                    <Image
                        src="/static/icons/xp.svg"
                        alt="XP"
                        width={16}
                        height={16}
                    />
                );
            case "time_spent":
                return (
                    <Image
                        src="/static/icons/clock.svg"
                        alt="Clock"
                        width={16}
                        height={16}
                    />
                );
            case "sessions_completed":
                return (
                    <Image
                        src="/static/icons/booknote.svg"
                        alt="Book"
                        width={16}
                        height={16}
                    />
                );
            case "perfect_quiz":
                return (
                    <Image
                        src="/static/icons/perfect-quiz.svg"
                        alt="Quiz"
                        width={16}
                        height={16}
                    />
                );
            case "quiz_success_rate":
                return (
                    <Image
                        src="/static/icons/quiz.svg"
                        alt="Quiz"
                        width={16}
                        height={16}
                    />
                );
            case "complete_part":
                return (
                    <Image
                        src="/static/icons/part.svg"
                        alt="Part"
                        width={16}
                        height={16}
                    />
                );
            case "time_based_session":
                return (
                    <Image
                        src="/static/icons/campfire.svg"
                        alt="Campfire"
                        width={16}
                        height={16}
                    />
                );
            case "community_post":
                return (
                    <Image
                        src="/static/icons/glob-community.svg"
                        alt="Community"
                        width={16}
                        height={16}
                    />
                );
            default:
                return (
                    <Image
                        src="/static/icons/pin.svg"
                        alt="Pin"
                        width={16}
                        height={16}
                    />
                );
        }
    };

    const getLabel = () => {
        const questKey = quest.questKey || quest.type;
        const labels = {
            // Types de quêtes
            xp_gain: t("quest_xp_gain", { value: quest.targetValue }),
            time_spent: t("quest_time_spent", { value: quest.targetValue }),
            sessions_completed: t("quest_sessions_completed", {
                value: quest.targetValue,
            }),
            perfect_quiz: t("quest_perfect_quiz"),
            quiz_success_rate: t("quest_quiz_success_rate", {
                value: quest.targetValue,
            }),
            complete_part: t("quest_complete_part"),
            evening_session: t("quest_evening_session"),
            community_post: t("quest_community_post"),
            // Quest keys spécifiques
            daily_xp: t("quest_xp_gain", { value: quest.targetValue }),
            daily_time: t("quest_time_spent", { value: quest.targetValue }),
            daily_sessions: t("quest_sessions_completed", {
                value: quest.targetValue,
            }),
            quiz_success: t("quest_quiz_success_rate", {
                value: quest.targetValue,
            }),
            // Reps : par type ET par questKey
            lessons_completed: t("quest_lessons_completed", {
                value: quest.targetValue,
            }),
            daily_reps_lessons: t("quest_lessons_completed", {
                value: quest.targetValue,
            }),
        };
        return labels[questKey] || labels[quest.type] || questKey;
    };

    const progressPercentage = Math.min(
        (quest.currentProgress / quest.targetValue) * 100,
        100,
    );

    const handleClaim = async () => {
        if (isClaiming) return;

        setIsClaiming(true);

        try {
            const response = await fetch("/api/quests/claim", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    questId: quest.id,
                }),
            });

            const data = await response.json();

            if (data.success) {
                // Afficher les récompenses
                setClaimedRewards(data.rewards);
                setEventPiece(data.eventPiece || null);
                setShowChestModal(true);

                // Notifier le parent
                if (onClaim) {
                    onClaim(quest.id, data);
                }
            } else {
                toast.error(data.message || t("quest_reward_error"));
            }
        } catch (error) {
            console.error("Error claiming reward:", error);
            toast.error(t("quest_reward_error"));
        } finally {
            setIsClaiming(false);
        }
    };

    // Déterminer l'état de la quête
    const isCompleted = quest.completed_at || quest.completed;
    const isRewarded = quest.rewarded;
    const canClaim = isCompleted && !isRewarded;
    // Pendant un événement de puzzles : ce que le coffre de cette quête contient en plus.
    const pieceChip = event && pieceLeft ? (
        <span className="inline-flex items-center gap-1 font-semibold text-lime-700 dark:text-lime-300">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/static/icons/puzzle-piece.svg" alt="" className="h-3.5 w-3.5" />
            {t("avatar_event_quest_piece")}
        </span>
    ) : null;

    // Fonction pour obtenir les couleurs en fonction de la rareté du coffre
    const getChestColors = (chestType) => {
        switch (chestType) {
            case "common":
                return {
                    primary: "from-gray-400/60 via-gray-500/40 to-gray-600/60",
                    secondary:
                        "from-gray-300/40 via-gray-400/30 to-gray-500/40",
                    particles: [
                        "bg-gray-300",
                        "bg-gray-400",
                        "bg-gray-500",
                        "bg-gray-600",
                    ],
                };
            case "uncommon":
                return {
                    primary:
                        "from-green-400/60 via-emerald-500/40 to-green-600/60",
                    secondary:
                        "from-green-300/40 via-emerald-400/30 to-green-500/40",
                    particles: [
                        "bg-green-300",
                        "bg-emerald-400",
                        "bg-green-500",
                        "bg-emerald-600",
                    ],
                };
            case "rare":
                return {
                    primary: "from-blue-400/60 via-cyan-500/40 to-blue-600/60",
                    secondary:
                        "from-blue-300/40 via-cyan-400/30 to-blue-500/40",
                    particles: [
                        "bg-blue-300",
                        "bg-cyan-400",
                        "bg-blue-500",
                        "bg-cyan-600",
                    ],
                };
            case "epic":
                return {
                    primary:
                        "from-purple-400/60 via-violet-500/40 to-purple-600/60",
                    secondary:
                        "from-purple-300/40 via-violet-400/30 to-purple-500/40",
                    particles: [
                        "bg-purple-300",
                        "bg-violet-400",
                        "bg-purple-500",
                        "bg-violet-600",
                    ],
                };
            case "legendary":
                return {
                    primary:
                        "from-orange-400/60 via-red-500/40 to-orange-600/60",
                    secondary:
                        "from-orange-300/40 via-red-400/30 to-orange-500/40",
                    particles: [
                        "bg-orange-300",
                        "bg-red-400",
                        "bg-orange-500",
                        "bg-red-600",
                    ],
                };
            case "gold":
                return {
                    primary:
                        "from-yellow-400/60 via-amber-500/40 to-orange-400/60",
                    secondary:
                        "from-yellow-300/40 via-amber-400/30 to-orange-300/40",
                    particles: [
                        "bg-yellow-300",
                        "bg-amber-400",
                        "bg-orange-300",
                        "bg-yellow-400",
                    ],
                };
            default: // normal
                return {
                    primary:
                        "from-yellow-400/60 via-amber-500/40 to-orange-400/60",
                    secondary:
                        "from-yellow-300/40 via-amber-400/30 to-orange-300/40",
                    particles: [
                        "bg-yellow-300",
                        "bg-amber-400",
                        "bg-orange-300",
                        "bg-yellow-400",
                    ],
                };
        }
    };

    const chestColors = getChestColors(quest.rewardChestType || "normal");

    // Coffre à gagner, bouton de réclamation ou coche : commun aux deux habillages.
    const rewardSlot =
                                canClaim ? (
                                    <motion.button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleClaim();
                                        }}
                                        disabled={isClaiming}
                                        className="relative group"
                                        whileHover={
                                            !isClaiming ? { scale: 1.15 } : {}
                                        }
                                        whileTap={
                                            !isClaiming ? { scale: 1.3 } : {}
                                        }
                                        animate={
                                            !isClaiming
                                                ? {
                                                      rotate: [
                                                          0, -10, 10, -10, 0,
                                                      ],
                                                  }
                                                : {}
                                        }
                                        transition={{
                                            rotate: {
                                                duration: 2,
                                                repeat: Infinity,
                                                ease: "easeInOut",
                                            },
                                        }}
                                    >
                                        <div className="relative">
                                            {isClaiming ? (
                                                <div className="w-6 h-6 flex items-center justify-center">
                                                    <motion.div
                                                        className="w-5 h-5 relative"
                                                        animate={{
                                                            rotate: 360,
                                                        }}
                                                        transition={{
                                                            duration: 1,
                                                            repeat: Infinity,
                                                            ease: "linear",
                                                        }}
                                                    >
                                                        <div className="w-full h-full rounded-full border-2 border-transparent border-t-yellow-500 border-r-yellow-400"></div>
                                                    </motion.div>
                                                    <motion.div
                                                        className="absolute inset-0 rounded-full border border-yellow-300"
                                                        animate={{
                                                            scale: [1, 1.2, 1],
                                                            opacity: [
                                                                0.8, 0.3, 0.8,
                                                            ],
                                                        }}
                                                        transition={{
                                                            duration: 1.5,
                                                            repeat: Infinity,
                                                            ease: "easeInOut",
                                                        }}
                                                    />
                                                    <motion.div
                                                        className="absolute inset-1 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full"
                                                        animate={{
                                                            opacity: [
                                                                0.6, 0.8, 0.6,
                                                            ],
                                                        }}
                                                        transition={{
                                                            duration: 2,
                                                            repeat: Infinity,
                                                            ease: "easeInOut",
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <Image
                                                    src={chestSrc}
                                                    alt={chestAlt || "Claim Reward"}
                                                    width={chestSize(24)}
                                                    height={chestSize(24)}
                                                    unoptimized
                                                    className="drop-shadow-lg"
                                                />
                                            )}
                                            {!isClaiming && (
                                                <>
                                                    {/* Aura principale */}
                                                    <motion.div
                                                        className={`absolute -inset-3 bg-gradient-to-r ${chestColors.primary} rounded-full blur-lg`}
                                                        animate={{
                                                            scale: [1, 1.2, 1],
                                                            opacity: [
                                                                0.6, 1, 0.6,
                                                            ],
                                                        }}
                                                        transition={{
                                                            duration: 2,
                                                            repeat: Infinity,
                                                            ease: "easeInOut",
                                                        }}
                                                    />
                                                    {/* Aura secondaire */}
                                                    <motion.div
                                                        className={`absolute -inset-4 bg-gradient-to-r ${chestColors.secondary} rounded-full blur-xl`}
                                                        animate={{
                                                            scale: [
                                                                1.1, 1.4, 1.1,
                                                            ],
                                                            opacity: [
                                                                0.3, 0.7, 0.3,
                                                            ],
                                                        }}
                                                        transition={{
                                                            duration: 3,
                                                            repeat: Infinity,
                                                            ease: "easeInOut",
                                                            delay: 0.5,
                                                        }}
                                                    />
                                                    {/* Particules scintillantes */}
                                                    <motion.div
                                                        className="absolute -inset-2 rounded-full"
                                                        animate={{
                                                            rotate: 360,
                                                        }}
                                                        transition={{
                                                            duration: 4,
                                                            repeat: Infinity,
                                                            ease: "linear",
                                                        }}
                                                    >
                                                        <div
                                                            className={`absolute top-0 left-1/2 w-1 h-1 ${chestColors.particles[0]} rounded-full animate-ping`}
                                                        />
                                                        <div
                                                            className={`absolute bottom-0 right-1/3 w-0.5 h-0.5 ${chestColors.particles[1]} rounded-full animate-ping`}
                                                            style={{
                                                                animationDelay:
                                                                    "0.5s",
                                                            }}
                                                        />
                                                        <div
                                                            className={`absolute left-0 top-1/3 w-0.5 h-0.5 ${chestColors.particles[2]} rounded-full animate-ping`}
                                                            style={{
                                                                animationDelay:
                                                                    "1s",
                                                            }}
                                                        />
                                                        <div
                                                            className={`absolute right-0 bottom-1/4 w-1 h-1 ${chestColors.particles[3]} rounded-full animate-ping`}
                                                            style={{
                                                                animationDelay:
                                                                    "1.5s",
                                                            }}
                                                        />
                                                    </motion.div>
                                                </>
                                            )}
                                        </div>
                                        <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gray-900 dark:bg-grayDark-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                                            {isClaiming
                                                ? t("quest_preparing_rewards")
                                                : t("quest_claim_reward")}
                                        </span>
                                    </motion.button>
                                ) : isRewarded ? (
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 500,
                                        }}
                                    >
                                        <div className="bg-green-500 rounded-full p-0.5">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="16"
                                                height="16"
                                                className="text-white"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    fill="currentColor"
                                                    d="m9.55 15.15l8.475-8.475q.3-.3.7-.3t.7.3t.3.713t-.3.712l-9.175 9.2q-.3.3-.7.3t-.7-.3L4.55 13q-.3-.3-.288-.712t.313-.713t.713-.3t.712.3z"
                                                />
                                            </svg>
                                        </div>
                                    </motion.div>
                                ) : quest.rewardChestType === "gold" ? (
                                    <Image
                                        src={chestSrc}
                                        alt={chestAlt || "Gold Chest"}
                                        width={chestSize(20)}
                                        height={chestSize(20)}
                                        unoptimized
                                    />
                                ) : (
                                    <Image
                                        src={chestSrc}
                                        alt={chestAlt || "Chest"}
                                        width={chestSize(20)}
                                        height={chestSize(20)}
                                        unoptimized
                                    />
                                );

    if (variant === "pastel") {
        return (
            <>
                <motion.div
                    className={`relative flex items-center gap-4 rounded-2xl p-4 ring-1 transition-colors duration-300 ${
                        isRewarded
                            ? "bg-emerald-50/70 ring-emerald-200/70 dark:bg-emerald-500/10 dark:ring-emerald-500/20"
                            : canClaim
                            ? "bg-amber-50 ring-amber-200/80 dark:bg-amber-500/10 dark:ring-amber-500/25"
                            : "bg-slate-50/80 ring-slate-900/5 dark:bg-white/5 dark:ring-white/10"
                    }${quest.type == "community_post" ? " cursor-pointer" : ""}`}
                    // Pas d'entrée propre : dans l'onglet Progression, c'est l'onglet
                    // entier qui arrive en fondu, comme Contenu et Discussions.
                    initial={false}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    onClick={() => {
                        if (quest.type == "community_post") {
                            router.push("/community");
                        }
                    }}
                >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white ring-1 ring-slate-900/5 dark:bg-white/10 dark:ring-white/10">
                        {getIcon()}
                    </div>

                    <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-3">
                            <p
                                className={`mb-0 text-sm font-semibold leading-tight ${
                                    isCompleted
                                        ? "text-emerald-800 dark:text-emerald-200"
                                        : "text-slate-900 dark:text-white"
                                }`}
                            >
                                {getLabel()}
                            </p>
                            <span className="shrink-0 text-xs font-medium tabular-nums text-slate-500 dark:text-grayDark-400">
                                {quest.currentProgress}/{quest.targetValue}
                            </span>
                        </div>

                        <div className="mt-2.5 h-2 rounded-full bg-slate-200/80 dark:bg-white/10">
                            <motion.div
                                className={`h-2 rounded-full ${
                                    isCompleted ? "bg-emerald-400" : "bg-primary"
                                }`}
                                initial={{ width: 0 }}
                                animate={{ width: `${progressPercentage}%` }}
                                transition={{ duration: 0.5, ease: "easeOut" }}
                            />
                        </div>

                        {/* Quête terminée, coffre à ouvrir : la ligne rappelle ce qu'il contient en plus. */}
                        {isCompleted && !isRewarded && pieceChip && <p className="mb-0 mt-2 flex items-center text-xs">{pieceChip}</p>}
                        {!isCompleted && (
                            <p className="mb-0 mt-2 flex items-center gap-1.5 text-xs text-slate-500 dark:text-grayDark-400">
                                <span>+{quest.rewardXp} XP</span>
                                {pieceChip && <span aria-hidden>·</span>}
                                {pieceChip}
                                {quest.difficulty && (
                                    <>
                                        <span aria-hidden>·</span>
                                        <span
                                            className={
                                                "font-medium " +
                                                (quest.difficulty === "easy"
                                                    ? "text-emerald-600 dark:text-emerald-400"
                                                    : quest.difficulty === "medium"
                                                    ? "text-amber-600 dark:text-amber-400"
                                                    : "text-red-600 dark:text-red-300")
                                            }
                                        >
                                            {t(`quest_difficulty_${quest.difficulty}`)}
                                        </span>
                                    </>
                                )}
                            </p>
                        )}
                    </div>

                    <div className="shrink-0">{rewardSlot}</div>
                </motion.div>

                <ChestOpeningModalV4
                    isOpen={showChestModal}
                    onClose={closeChest}
                    rewards={claimedRewards}
                    chestType={quest.rewardChestType || "normal"}
                    extraRewards={eventPiece ? PUZZLE_REWARD : null}
                    eventChests={eventChests}
                eventPieceSound={event ? eventSounds(event).pick : null}
                />
                {eventPiece && <EventPieceReveal piece={showEventPiece ? eventPiece : null} onClose={closeEventPiece} />}
            </>
        );
    }

    return (
        <>
            <motion.div
                className={`rounded-lg border p-3 transition-all duration-300 relative ${
                    isRewarded
                        ? "bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-700/50 shadow-green-100/50 dark:shadow-green-900/20 shadow-lg"
                        : canClaim
                        ? "bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border-yellow-200 dark:border-yellow-700/50 shadow-yellow-100/50 dark:shadow-yellow-900/20 shadow-lg"
                        : "bg-white dark:bg-white/5 border-gray-100 dark:border-white/10"
                }${quest.type == "community_post" ? " cursor-pointer" : ""}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.01 }}
                onClick={() => {
                    if (quest.type == "community_post") {
                        router.push("/community");
                    }
                }}
            >
                {isRewarded && (
                    <div
                        className="absolute inset-0 pointer-events-none opacity-[0.06]"
                        style={{
                            backgroundImage: "repeating-linear-gradient(45deg, #10b981, #10b981 1px, transparent 1px, transparent 8px)"
                        }}
                    />
                )}
                <div className="flex items-start gap-3 relative">
                    <div className="w-5 h-5 flex items-center justify-center text-base flex-shrink-0">
                        {getIcon()}
                    </div>

                    <div className="flex-1 min-w-0 relative z-10">
                        <div
                            className={`font-medium text-sm mb-2 leading-tight ${
                                quest.completed
                                    ? "text-green-800 dark:text-green-200 font-semibold"
                                    : "text-gray-900 dark:text-white"
                            }`}
                        >
                            {getLabel()}
                        </div>

                        <div className="flex items-center gap-3 mb-2">
                            <div className="relative flex-1 bg-gray-200 dark:bg-white/10 rounded-full h-4">
                                <motion.div
                                    className={`${
                                        quest.completed
                                            ? "bg-emerald-400"
                                            : "bg-yellow-400"
                                    } h-4 rounded-full`}
                                    initial={{ width: 0 }}
                                    animate={{
                                        width: `${progressPercentage}%`,
                                    }}
                                    transition={{ duration: 0.5 }}
                                />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span
                                        className={`text-xs ${
                                            quest.completed
                                                ? "text-emerald-800 dark:text-emerald-950"
                                                : "text-gray-600 dark:text-white dark:bg-black/45 dark:px-1.5 dark:rounded-full"
                                        } font-medium`}
                                    >
                                        {quest.currentProgress} /{" "}
                                        {quest.targetValue}
                                    </span>
                                </div>
                            </div>

                            <div className="flex-shrink-0">
                                {rewardSlot}
                            </div>
                        </div>

                        {/* Affichage de la récompense XP */}
                        {/* Quête terminée, coffre à ouvrir : la ligne rappelle ce qu'il contient en plus. */}
                        {isCompleted && !isRewarded && pieceChip && <p className="mb-0 mt-2 flex items-center text-xs">{pieceChip}</p>}
                        {!isCompleted && (
                            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-grayDark-300">
                                <span>+{quest.rewardXp} XP</span>
                                {pieceChip && <span aria-hidden>·</span>}
                                {pieceChip}
                                {quest.difficulty && (
                                    <>
                                        <span>•</span>
                                        <span
                                            className={`font-medium ${
                                                quest.difficulty === "easy"
                                                    ? "text-green-600 dark:text-green-400"
                                                    : quest.difficulty ===
                                                      "medium"
                                                    ? "text-yellow-600 dark:text-yellow-400"
                                                    : "text-red-600 dark:text-red-300"
                                            }`}
                                        >
                                            {t(
                                                `quest_difficulty_${quest.difficulty}`,
                                            )}
                                        </span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>

            {/* Modal d'ouverture de coffre hybride */}
            <ChestOpeningModalV4
                isOpen={showChestModal}
                onClose={closeChest}
                rewards={claimedRewards}
                chestType={quest.rewardChestType || "normal"}
                extraRewards={eventPiece ? PUZZLE_REWARD : null}
                eventChests={eventChests}
                eventPieceSound={event ? eventSounds(event).pick : null}
            />
            {eventPiece && <EventPieceReveal piece={showEventPiece ? eventPiece : null} onClose={closeEventPiece} />}
        </>
    );
}
